import { useRef, useState } from "react";
import type { Shape, ShapeKind } from "@/types";
import { SHAPE_COLORS } from "@/constants";

const STROKE = "rgba(0,0,0,0.14)";
const STROKE_W = 1.5;

/** Renders the shape geometry as an SVG that fills its container. */
export function ShapeGlyph({
  kind,
  color,
  width,
  height,
}: {
  kind: ShapeKind;
  color: string;
  width: number | string;
  height: number | string;
}) {
  // Use a fixed 100x100 viewBox; preserveAspectRatio="none" stretches it to fit
  // any width/height so rectangles and ellipses scale cleanly.
  const common = {
    fill: color,
    stroke: STROKE,
    strokeWidth: STROKE_W,
    vectorEffect: "non-scaling-stroke" as const,
  };
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible" }}
    >
      {(kind === "rectangle" || kind === "square") && (
        <rect x={1} y={1} width={98} height={98} rx={6} {...common} />
      )}
      {kind === "circle" && <ellipse cx={50} cy={50} rx={49} ry={49} {...common} />}
      {kind === "triangle" && (
        <polygon points="50,2 98,98 2,98" strokeLinejoin="round" {...common} />
      )}
    </svg>
  );
}

export function ShapeCard({
  shape,
  scaleRef,
  selected,
  onSelect,
  onMove,
  onSavePosition,
  onResize,
  onSaveSize,
  onColorChange,
}: {
  shape: Shape;
  scaleRef: React.RefObject<number>;
  selected: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onSavePosition: (id: string, x: number, y: number) => void;
  onResize: (id: string, w: number, h: number) => void;
  onSaveSize: (id: string, w: number, h: number) => void;
  onColorChange: (id: string, color: string) => void;
}) {
  const [lifted, setLifted] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragging = useRef(false);
  const resizingRef = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });
  const shapeRef = useRef(shape);
  shapeRef.current = shape;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onSaveRef = useRef(onSavePosition);
  onSaveRef.current = onSavePosition;
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const onSaveSizeRef = useRef(onSaveSize);
  onSaveSizeRef.current = onSaveSize;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(shape._id);
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    setLifted(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - lastMouse.current.x) / s;
    const dy = (e.clientY - lastMouse.current.y) / s;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    const sh = shapeRef.current;
    onMoveRef.current(sh._id, sh.x + dx, sh.y + dy);
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setLifted(false);
    const sh = shapeRef.current;
    onSaveRef.current(sh._id, sh.x, sh.y);
  };

  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(shape._id);
    resizingRef.current = true;
    resizeStart.current = { mx: e.clientX, my: e.clientY, w: shape.w, h: shape.h };
    e.currentTarget.setPointerCapture(e.pointerId);
    setResizing(true);
  };

  const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - resizeStart.current.mx) / s;
    const dy = (e.clientY - resizeStart.current.my) / s;
    const newW = Math.max(30, resizeStart.current.w + dx);
    const newH = Math.max(30, resizeStart.current.h + dy);
    onResizeRef.current(shapeRef.current._id, newW, newH);
  };

  const onResizePointerUp = () => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    setResizing(false);
    const sh = shapeRef.current;
    onSaveSizeRef.current(sh._id, sh.w, sh.h);
  };

  const showHandle = selected || hovered || resizing;

  return (
    <div
      style={{
        position: "absolute",
        left: shape.x,
        top: shape.y,
        width: shape.w,
        height: shape.h,
        zIndex: lifted || resizing ? 12 : 4,
        pointerEvents: "auto",
        cursor: lifted ? "grabbing" : "grab",
        filter: lifted
          ? "drop-shadow(0 12px 28px rgba(0,0,0,0.28))"
          : "drop-shadow(0 2px 6px rgba(0,0,0,0.10))",
        transform: lifted ? "scale(1.04)" : "scale(1)",
        transition: lifted
          ? "transform 0.08s ease-out, filter 0.08s ease-out"
          : "transform 0.18s ease-out, filter 0.18s ease-out",
        outline: selected ? "2px solid #0091ff" : "2px solid transparent",
        outlineOffset: 3,
        borderRadius: 4,
        touchAction: "none",
        userSelect: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseDown={(e) => { e.stopPropagation(); onSelect(shape._id); }}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {selected && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "6px 10px",
            background: "white",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.07)",
            pointerEvents: "auto",
            whiteSpace: "nowrap",
            zIndex: 20,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {SHAPE_COLORS.map((c) => (
            <button
              key={c}
              onClick={(e) => { e.stopPropagation(); onColorChange(shape._id, c); }}
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: shape.color === c
                  ? "2.5px solid #0091ff"
                  : "1.5px solid rgba(0,0,0,0.18)",
                background: c,
                cursor: "pointer",
                padding: 0,
                flexShrink: 0,
                boxSizing: "border-box",
                outline: "none",
                boxShadow: shape.color === c ? "0 0 0 1.5px white inset" : "none",
              }}
            />
          ))}
        </div>
      )}
      <ShapeGlyph kind={shape.kind} color={shape.color} width="100%" height="100%" />
      {/* Resize handle */}
      <div
        style={{
          position: "absolute",
          bottom: -5,
          right: -5,
          width: 10,
          height: 10,
          borderRadius: 2,
          background: selected ? "#0091ff" : "rgba(0,0,0,0.4)",
          cursor: "nwse-resize",
          touchAction: "none",
          opacity: showHandle ? 1 : 0,
          transition: "opacity 0.15s",
          border: "1.5px solid white",
        }}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
      />
    </div>
  );
}
